import { isDefined, isNotDefined } from '@sgme/fp';
import type { Action } from 'state/actions';
import type { BatchFormReloadCorrelation } from 'state/batchForm/batchForm.action';
import type { ScheduledRun, ScheduledRunFields, ScheduledRunState, ScheduleOperation } from 'state/scheduledRuns/state';
import { initialOccurrenceReloadState, initialScheduledRunState, initScheduleState } from 'state/scheduledRuns/state';
import { addOrPatchKey, addOrReplaceKey, removeKey } from 'state/utils/collection';
import type { Patcher } from 'state/utils/types';
import { asSuccess } from 'utils/status';

const scheduleAddOrPatchKey = (
  state: ScheduledRunState,
  id: string,
  patcher: Patcher<ScheduledRun>,
  field?: keyof ScheduledRunFields,
) => ({
  ...state,
  runs: addOrPatchKey(
    state.runs,
    id,
    s => {
      if (field) {
        s = { ...s, pristineFields: s.pristineFields.filter(f => f !== field) };
      }
      return patcher(s);
    },
    initScheduleState(),
  ),
});
export function scheduledRunReducer(
  state: ScheduledRunState = initialScheduledRunState,
  action: Action,
): ScheduledRunState {
  switch (action.type) {
    case 'SCHEDULED_RUN_INIT':
      return { ...state, runs: addOrReplaceKey(state.runs, action.id, action.run) };
    case 'SCHEDULED_RUN_TITLE_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({
          ...s,
          title: action.title,
        }),
        'title',
      );
    case 'SCHEDULED_RUN_COMMENT_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({
          ...s,
          comment: action.comment,
        }),
        'comment',
      );
    case 'SCHEDULED_RUN_READERS_CHANGED':
      return scheduleAddOrPatchKey(state, action.id, s => ({ ...s, readers: action.readers }), 'readers');
    case 'SCHEDULED_RUN_DAYS_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({
          ...s,
          days: action.days,
        }),
        'days',
      );
    case 'SCHEDULED_RUN_TIME_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({
          ...s,
          time: action.time,
        }),
        'time',
      );
    case 'SCHEDULED_RUN_RESET_TYPE':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        type: undefined,
        createRun: { status: 'idle', error: undefined, content: undefined },
        runs: { status: 'idle', error: undefined, content: [] },
        updateRun: { status: 'idle', error: undefined, content: undefined },
      }));
    case 'SCHEDULED_RUN_NEW_SCHEDULE':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        quotes: action.quotes,
        type: 'editSchedule' as ScheduleOperation,
      }));
    case 'SCHEDULED_RUN_TRIGGER_MODE_CHANGED':
      return scheduleAddOrPatchKey(state, action.id, s => ({ ...s, triggerMode: action.triggerMode }));
    case 'SCHEDULED_RUN_FREQUENCY_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({ ...s, frequency: { ...s.frequency, type: action.frequency } }),
        'frequency',
      );
    case 'SCHEDULED_RUN_FREQUENCY_TIME_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({
          ...s,
          frequency: { ...s.frequency, value: action.frequencyTime },
        }),
        'frequency',
      );
    case 'SCHEDULED_RUN_DATES_RANGE_CHANGED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s => ({
          ...s,
          range:
            isDefined(action.startDate) && isDefined(action.endDate)
              ? { startDate: action.startDate, endDate: action.endDate }
              : undefined,
        }),
        'range',
      );
    case 'SCHEDULED_RUN_SELECTED_RUN':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        runs: {
          ...s.runs,
          content: asSuccess(s.runs)?.content.map(run => ({ ...run, isSelected: action.runId === run.id })) ?? [],
        },
        occurrenceModalOpen: false,
        occurrences: { status: 'idle' },
        occurrenceDetail: { status: 'idle' },
        selectedOccurrenceId: undefined,
        occurrenceRequestId: undefined,
        occurrenceRunId: undefined,
        occurrenceDetailRequestId: undefined,
        occurrenceDetailOccurrenceId: undefined,
        occurrenceReload: initialOccurrenceReloadState,
      }));
    case 'SCHEDULED_RUN_SET_OPERATION':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        type: action.operation,
      }));
    case 'SCHEDULED_RUN_SET_QUOTES':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        quotes: action.quotes,
      }));
    case 'SCHEDULED_RUN_ADD_TO_SCHEDULE':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        quotes: action.quotes,
        type: 'updateQuotes' as ScheduleOperation,
      }));
    case 'SCHEDULED_RUN_DELETE_SCHEDULE_REQUESTED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        deleteRun: { status: 'loading', error: undefined, content: undefined },
      }));
    case 'SCHEDULED_RUN_DELETE_SCHEDULE_FAILED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        deleteRun: { status: 'error', error: action.error, content: undefined },
      }));
    case 'SCHEDULED_RUN_DELETE_SCHEDULE_SUCCEEDED':
      return { ...state, runs: removeKey(state.runs, action.id) };

    case 'SCHEDULED_RUN_ADD_TO_SCHEDULE_REQUESTED':
    case 'SCHEDULED_RUN_UPDATE_SCHEDULE_REQUESTED':
    case 'SCHEDULED_RUN_UPDATE_QUOTES_REQUESTED':
    case 'SCHEDULED_RUN_PAUSE_SCHEDULE_REQUESTED':
    case 'SCHEDULED_RUN_RESCHEDULE_REQUESTED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        updateRun: { status: 'loading', error: undefined, content: undefined },
      }));
    case 'SCHEDULED_RUN_ADD_TO_SCHEDULE_SUCCEEDED':
    case 'SCHEDULED_RUN_UPDATE_SCHEDULE_SUCCEEDED':
    case 'SCHEDULED_RUN_UPDATE_QUOTES_SUCCEEDED':
    case 'SCHEDULED_RUN_PAUSE_SCHEDULE_SUCCEEDED':
    case 'SCHEDULED_RUN_RESCHEDULE_SUCCEEDED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        updateRun: { status: 'success', error: undefined, content: undefined },
      }));
    case 'SCHEDULED_RUN_ADD_TO_SCHEDULE_FAILED':
    case 'SCHEDULED_RUN_UPDATE_SCHEDULE_FAILED':
    case 'SCHEDULED_RUN_UPDATE_QUOTES_FAILED':
    case 'SCHEDULED_RUN_PAUSE_SCHEDULE_FAILED':
    case 'SCHEDULED_RUN_RESCHEDULE_FAILED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            updateRun: { status: 'error', error: action.error },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_NAME_CHECKED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            existingTitle: action.existingTitle,
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_CREATION_REQUESTED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            createRun: { status: 'loading' },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_CREATION_SUCCEEDED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            createRun: { status: 'success', content: action.createResponse },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_CREATION_FAILED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            createRun: { status: 'error', error: action.error },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_RUNS_REQUESTED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            runs: { status: 'loading' },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_RUNS_RECEIVED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            runs: {
              status: 'success',
              content: action.runs,
            },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_RUNS_FAILED':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            runs: { status: 'error', error: action.error },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_SET_CONFIRM_BANNER':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            confirmBanner: action.confirmBanner,
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_VALIDATE_QUOTES':
      return scheduleAddOrPatchKey(
        state,
        action.id,
        s =>
          ({
            ...s,
            addQuote: { status: 'loading' },
          }) satisfies ScheduledRun,
      );
    case 'SCHEDULED_RUN_VALIDATE_QUOTES_SUCCEEDED':
      const updatedState = scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        quotes: [
          ...s.quotes,
          ...(action.validationResult.results?.filter(q => q.isValid).map(({ isValid: _, ...quote }) => quote) ?? []),
        ],
        addQuote: { content: action.validationResult, status: 'success' },
      }));
      const payoffs = action.validationResult.results?.reduce((acc, res) => {
        if (!isDefined(res.productType) || !isDefined(res.productSubType) || isDefined(res.quoteId)) {
          return acc;
        }
        if (!acc[res.productType]) {
          acc[res.productType] = {};
        }
        acc[res.productType][res.productSubType] = res.isValid;
        return acc;
      }, updatedState.payoffs);
      return { ...updatedState, payoffs: { ...payoffs } };
    case 'SCHEDULED_RUN_VALIDATE_QUOTES_FAILED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        addQuote: { content: undefined, status: 'error', error: action.error },
      }));
    case 'SCHEDULED_RUN_OCCURRENCES_REQUESTED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        occurrenceModalOpen: true,
        occurrences: { status: 'loading' },
        occurrenceDetail: { status: 'idle' },
        selectedOccurrenceId: undefined,
        occurrenceRequestId: action.requestId,
        occurrenceRunId: action.runId,
        occurrenceDetailRequestId: undefined,
        occurrenceDetailOccurrenceId: undefined,
        occurrenceReload: initialOccurrenceReloadState,
      }));
    case 'SCHEDULED_RUN_OCCURRENCES_RECEIVED':
      return scheduleAddOrPatchKey(state, action.id, s => {
        if (s.occurrenceRequestId !== action.requestId || s.occurrenceRunId !== action.runId) {
          return s;
        }
        return {
          ...s,
          occurrences: { status: 'success', content: action.occurrences },
        };
      });
    case 'SCHEDULED_RUN_OCCURRENCES_FAILED':
      return scheduleAddOrPatchKey(state, action.id, s => {
        if (s.occurrenceRequestId !== action.requestId || s.occurrenceRunId !== action.runId) {
          return s;
        }
        return {
          ...s,
          occurrences: { status: 'error', error: action.error },
        };
      });
    case 'SCHEDULED_RUN_OCCURRENCE_DETAIL_REQUESTED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        occurrenceDetail: { status: 'loading' },
        occurrenceDetailRequestId: action.requestId,
        occurrenceDetailOccurrenceId: action.occurrenceId,
      }));
    case 'SCHEDULED_RUN_OCCURRENCE_DETAIL_RECEIVED':
      return scheduleAddOrPatchKey(state, action.id, s => {
        if (
          s.occurrenceRunId !== action.runId ||
          s.selectedOccurrenceId !== action.occurrenceId ||
          s.occurrenceDetailRequestId !== action.requestId
        ) {
          return s;
        }
        return {
          ...s,
          occurrenceDetail: { status: 'success', content: action.occurrence },
        };
      });
    case 'SCHEDULED_RUN_OCCURRENCE_DETAIL_FAILED':
      return scheduleAddOrPatchKey(state, action.id, s => {
        if (
          s.occurrenceRunId !== action.runId ||
          s.selectedOccurrenceId !== action.occurrenceId ||
          s.occurrenceDetailRequestId !== action.requestId
        ) {
          return s;
        }
        return {
          ...s,
          occurrenceDetail: { status: 'error', error: action.error },
        };
      });
    case 'SCHEDULED_RUN_OCCURRENCE_SELECTED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        selectedOccurrenceId: action.occurrenceId,
        occurrenceDetail: { status: 'idle' },
        occurrenceDetailRequestId: undefined,
        occurrenceDetailOccurrenceId: undefined,
      }));
    case 'SCHEDULED_RUN_OCCURRENCE_RELOAD_STARTED':
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        occurrenceReload: {
          status: 'loading',
          operationId: action.operationId,
          tabIds: action.tabIds,
          queuedQuoteIds: action.queuedQuoteIds,
          queuedQuoteIdsByTabId: action.queuedQuoteIdsByTabId,
          pendingByTemporaryFormId: {},
          succeededQuoteIds: [],
          failedQuoteIds: [],
          skippedQuoteIds: action.skippedQuoteIds,
          skippedCount: action.skippedCount,
        },
      }));
    case 'SCHEDULED_RUN_OCCURRENCE_RELOAD_RESET': {
      const scheduledRun = state.runs[action.id];
      if (isDefined(action.operationId) && scheduledRun?.occurrenceReload.operationId !== action.operationId) {
        return state;
      }
      return scheduleAddOrPatchKey(state, action.id, s => ({
        ...s,
        occurrenceModalOpen: false,
        occurrences: { status: 'idle' },
        occurrenceDetail: { status: 'idle' },
        selectedOccurrenceId: undefined,
        occurrenceRequestId: undefined,
        occurrenceRunId: undefined,
        occurrenceDetailRequestId: undefined,
        occurrenceDetailOccurrenceId: undefined,
        occurrenceReload: initialOccurrenceReloadState,
      }));
    }
    case 'WORKSPACE_TAB_REMOVED':
      return action.workspace === 'newBatch' ? resetOccurrenceReloadForTab(state, action.id) : state;
    case 'WORKSPACE_TABS_ALL_REMOVED':
      return action.workspace === 'newBatch' ? resetOccurrenceReloadForTabs(state, action.tabIds) : state;
    case 'BATCH_FORM_RELOAD_REQUESTED':
      return updateOccurrenceReloadFromBatchRequest(state, action);
    case 'DEFAULT_BATCH_FORM_RECEIVED':
      return updateOccurrenceReloadFromBatchResult(
        state,
        action.temporaryFormId,
        true,
        action.tabId,
        action.reloadCorrelation,
      );
    case 'BATCH_FORM_RELOAD_FAILED':
      return updateOccurrenceReloadFromBatchResult(
        state,
        action.temporaryFormId,
        false,
        action.tabId,
        action.reloadCorrelation,
      );
    default:
      return state;
  }
}

function updateOccurrenceReloadFromBatchRequest(
  state: ScheduledRunState,
  action: Extract<Action, { type: 'BATCH_FORM_RELOAD_REQUESTED' }>,
): ScheduledRunState {
  if (isNotDefined(action.reloadCorrelation)) {
    return state;
  }

  for (const [id, scheduledRun] of Object.entries(state.runs)) {
    const reload = scheduledRun.occurrenceReload;
    if (
      reload.status !== 'loading' ||
      reload.operationId !== action.reloadCorrelation.operationId ||
      !reload.tabIds.includes(action.tabId) ||
      id !== action.reloadCorrelation.scheduledRunId ||
      !reload.queuedQuoteIdsByTabId[action.tabId]?.includes(action.reloadCorrelation.quoteId) ||
      Object.hasOwn(reload.pendingByTemporaryFormId, action.temporaryFormId)
    ) {
      continue;
    }

    return {
      ...state,
      runs: {
        ...state.runs,
        [id]: {
          ...scheduledRun,
          occurrenceReload: {
            ...reload,
            queuedQuoteIds: removeFirst(reload.queuedQuoteIds, action.reloadCorrelation.quoteId),
            queuedQuoteIdsByTabId: {
              ...reload.queuedQuoteIdsByTabId,
              [action.tabId]: removeFirst(
                reload.queuedQuoteIdsByTabId[action.tabId] ?? [],
                action.reloadCorrelation.quoteId,
              ),
            },
            pendingByTemporaryFormId: {
              ...reload.pendingByTemporaryFormId,
              [action.temporaryFormId]: {
                quoteId: action.reloadCorrelation.quoteId,
                tabId: action.tabId,
              },
            },
          },
        },
      },
    };
  }

  return state;
}

function updateOccurrenceReloadFromBatchResult(
  state: ScheduledRunState,
  temporaryFormId: string,
  succeeded: boolean,
  tabId?: string,
  reloadCorrelation?: BatchFormReloadCorrelation,
): ScheduledRunState {
  if (isNotDefined(tabId) || isNotDefined(reloadCorrelation)) {
    return state;
  }

  for (const [id, scheduledRun] of Object.entries(state.runs)) {
    const reload = scheduledRun.occurrenceReload;
    const pendingQuote = reload.pendingByTemporaryFormId[temporaryFormId];
    if (
      reload.status !== 'loading' ||
      reload.operationId !== reloadCorrelation.operationId ||
      id !== reloadCorrelation.scheduledRunId ||
      pendingQuote?.tabId !== tabId ||
      pendingQuote.quoteId !== reloadCorrelation.quoteId
    ) {
      continue;
    }

    const pendingByTemporaryFormId = { ...reload.pendingByTemporaryFormId };
    delete pendingByTemporaryFormId[temporaryFormId];
    const nextReload = {
      ...reload,
      pendingByTemporaryFormId,
      succeededQuoteIds: succeeded ? [...reload.succeededQuoteIds, pendingQuote.quoteId] : reload.succeededQuoteIds,
      failedQuoteIds: succeeded ? reload.failedQuoteIds : [...reload.failedQuoteIds, pendingQuote.quoteId],
    };

    return {
      ...state,
      runs: {
        ...state.runs,
        [id]: {
          ...scheduledRun,
          occurrenceReload:
            nextReload.queuedQuoteIds.length === 0 && Object.keys(nextReload.pendingByTemporaryFormId).length === 0
              ? { ...nextReload, status: 'success' }
              : nextReload,
        },
      },
    };
  }

  return state;
}

function removeFirst(values: readonly string[], value: string): readonly string[] {
  const index = values.indexOf(value);
  return index < 0 ? values : [...values.slice(0, index), ...values.slice(index + 1)];
}

function resetOccurrenceReloadForTab(state: ScheduledRunState, tabId: string): ScheduledRunState {
  return resetOccurrenceReloadForTabs(state, [tabId]);
}

function resetOccurrenceReloadForTabs(state: ScheduledRunState, tabIds: readonly string[]): ScheduledRunState {
  const tabIdSet = new Set(tabIds);
  let changed = false;
  const runs = Object.fromEntries(
    Object.entries(state.runs).map(([id, scheduledRun]) => {
      const reload = scheduledRun.occurrenceReload;
      const removedTabIds = reload.tabIds.filter(tabId => tabIdSet.has(tabId));
      if (removedTabIds.length > 0) {
        const remainingTabIds = reload.tabIds.filter(tabId => !tabIdSet.has(tabId));
        if (remainingTabIds.length === 0) {
          changed = true;
          return [
            id,
            {
              ...scheduledRun,
              occurrenceModalOpen: false,
              occurrenceReload: initialOccurrenceReloadState,
            },
          ];
        }

        const queuedQuoteIdsByTabId = Object.fromEntries(
          remainingTabIds.map(tabId => [tabId, reload.queuedQuoteIdsByTabId[tabId] ?? []]),
        );
        const pendingByTemporaryFormId = Object.fromEntries(
          Object.entries(reload.pendingByTemporaryFormId).filter(
            ([, pendingQuote]) => !tabIdSet.has(pendingQuote.tabId),
          ),
        );
        const cancelledQuoteIds = [
          ...removedTabIds.flatMap(tabId => reload.queuedQuoteIdsByTabId[tabId] ?? []),
          ...Object.values(reload.pendingByTemporaryFormId)
            .filter(pendingQuote => tabIdSet.has(pendingQuote.tabId))
            .map(pendingQuote => pendingQuote.quoteId),
        ];
        const queuedQuoteIds = remainingTabIds.flatMap(tabId => queuedQuoteIdsByTabId[tabId] ?? []);
        const status =
          queuedQuoteIds.length === 0 && Object.keys(pendingByTemporaryFormId).length === 0 ? 'success' : 'loading';

        changed = true;
        return [
          id,
          {
            ...scheduledRun,
            occurrenceReload: {
              ...reload,
              status,
              tabIds: remainingTabIds,
              queuedQuoteIds,
              queuedQuoteIdsByTabId,
              pendingByTemporaryFormId,
              failedQuoteIds: [...reload.failedQuoteIds, ...cancelledQuoteIds],
            },
          },
        ];
      }
      return [id, scheduledRun];
    }),
  );
  return changed ? { ...state, runs } : state;
}
